import React from "react";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { MUTATION_TYPE, useToast } from "hooks";
import { UPDATE_USER } from "graphql/mutations";
import { GET_ME } from "graphql/queries";
import { USER_NAME_MAX_LENGTH } from "constants/index";
import { ModalForm, MODAL_SIZE } from "components/Modal";
import { SIZE, TYPE } from "components/Form";

/**
 * NameChangeModal
 *
 * Lets the signed-in user change their first and last name.
 *
 * @param {string}   updateId   Id of the user being updated
 * @param {string}   firstName  Current first name, prefilled in the form
 * @param {string}   lastName   Current last name, prefilled in the form
 * @param {function} onClose
 */
const NameChangeModal = ({ updateId, firstName, lastName, onClose, ...props }) => {
  const { toast } = useToast();

  return (
    <ModalForm
      mutationType={MUTATION_TYPE.update}
      updateId={updateId}
      initialValues={{ firstName, lastName }}
      mutationData={{ mutationName: { update: UPDATE_USER }, refetchQueries: [GET_ME], refetchAfterMutate: true }}
      onCompleted={() => toast.success(<FormattedMessage id="UserSettings.Account.Name.SaveSuccess" />)}
      onError={() => toast.error(<FormattedMessage id="UserSettings.Account.Name.SaveError" />)}
      onClose={onClose}
      title={<FormattedMessage id="UserSettings.Account.Name.Modal.Title" />}
      data={FORM_DATA}
      size={MODAL_SIZE.sm}
      primaryButtonMessageId="Global.Save"
      {...props}
    />
  );
};

const nameField = (name, labelId) => ({
  label: <FormattedMessage id={labelId} />,
  type: TYPE.input,
  size: SIZE.xlarge,
  properties: {
    name,
    type: "text",
    required: true,
    maxLength: USER_NAME_MAX_LENGTH,
    validate: (value) => Boolean(value?.trim()),
  },
});

const FORM_DATA = [
  {
    style: css`
      width: 100%;
    `,
    items: [
      nameField("firstName", "UserSettings.Account.Name.FirstName"),
      nameField("lastName", "UserSettings.Account.Name.LastName"),
    ],
  },
];

NameChangeModal.propTypes = {
  updateId: PropTypes.string.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default NameChangeModal;
